import React, { useCallback, useMemo } from 'react';
import { AppId, FCX, ID, ProductLogo, usePlatformContext } from '@datassential/platform-ui-lib';
import { useHomeCategoryLensAllMacs } from '@apiHooks/useHomeCategoryLensAllMacs';
import { useMacsPermissionLevel } from '@apiHooks/useMacsPermissionLevel';
import { PermissionLevelEnum } from '@models/PermissionSection';
import { Grid } from '@components/new/Grid/Grid';
import HomeSectionSubTitle from '@pages/Home/components/HomeSectionSubTitle';
import HomeSectionFooter from '@pages/Home/components/HomeSectionFooter';
import AccessLink from "@components/AccessLink/AccessLink";
import PublicationCard, { PublicationCardTooltip } from '@components/PublicationCard/PublicationCard';
import { sanitizeUrl } from "@helpers";
import { routeNames } from "@core/routeNames";
import { requestTrialByFeature } from "@actions/page";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import dsPlaceholderLogo from "../../../../img/ds-placeholder-logo.svg";

interface CategoryLensMacsProps {
    categoryName: string;
    categoryId: ID;
}

const CategoryLensMacs: FCX<CategoryLensMacsProps> = ({ categoryName, categoryId }) => {
    const dispatch = useDispatch();
    const { data, isLoading } = useHomeCategoryLensAllMacs();
    const reports = useMemo(
        () => data.items.get(categoryId) ?? data.items.get(0 as ID) ?? [],
        [data, categoryId],
    );
    const { data: permissionLevel } = useMacsPermissionLevel();
    const hasAccess = useMemo(() => permissionLevel === PermissionLevelEnum.Full, [permissionLevel]);
    const { userInfo: { snapUiUrl, snapMacUrl } } = usePlatformContext();
    const detailsPath = sanitizeUrl(`${snapUiUrl}/${snapMacUrl}`);


    const requestTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('macs'));
    }, [dispatch]);

    return (
        <div className="CategoryLensMacs" data-testid="blockCategoryLensMacs">
            <HomeSectionSubTitle testId="textCategoryLensMacsTitle">
                MAC: {categoryName}
            </HomeSectionSubTitle>
            <div className="CategoryLensMacs__container">
                {isLoading && (
                    <div className="CategoryLensMacs__loading">
                        <div className="spinner spinner-blink">
                            <ProductLogo appId={AppId.Snap} type="regular"/>
                        </div>
                    </div>
                )}
                <div className="CategoryLensMacs__grid">
                    <Grid itemWidth={230} maxRowsCount={1}>
                        {reports.map((item, index) => ({
                            key: `${categoryName}-${item.id}`,
                            value: (
                                <React.Fragment>
                                    <PublicationCard
                                        className={`CategoryLensMacs__card${!hasAccess ? ' CategoryLensMacs__card--locked' : ''}`}
                                        data={item}
                                        index={index}
                                        link={hasAccess ? `${detailsPath}${item.id}` : ''}
                                        tooltipId={`category-lens-mac-${item.id}`}
                                        testIdBase="CategoryLensMacCard"
                                        placeholder={<img src={dsPlaceholderLogo as string} alt="" />}
                                    />
                                    <PublicationCardTooltip
                                        className="CategoryLensMacs__card-tooltip"
                                        data={item}
                                        index={index}
                                        tooltipId={`category-lens-mac-${item.id}`}
                                        testIdBase="CategoryLensMacCard"
                                    />
                                </React.Fragment>
                            ),
                        }))}
                    </Grid>
                </div>
            </div>
            <HomeSectionFooter className="CategoryLensMacs__footer">
                <AccessLink
                    url={sanitizeUrl(`${snapUiUrl}/${routeNames.trendingNow}#section-mac`)}
                    text="More MACs"
                    altText="Upgrade your subscription to unlock the Menu Adoption Cycle Reports."
                    hasAccess={hasAccess}
                    onContactUsClick={requestTrial}
                    testId="linkCategoryLensMoreMacs"
                />
            </HomeSectionFooter>
        </div>
    );
};

export default CategoryLensMacs;
